import React from 'react';

import { IconButton } from '@mui/material';
import { AlineTooltip } from 'src/components';
import { CloseIcon } from 'src/components/icons/CloseIcon';
import { UndoIcon } from 'src/components/icons/UndoIcon';
import { makeStyles } from 'tss-react/mui';

/**
 * Shared layout for every row of the report plan the user approves: a fixed
 * icon slot, the column's text, and actions on the right. The two kinds of row
 * render different middles, so they share the frame rather than a component.
 */
export const useReportPlanRowStyles = makeStyles<void, 'hoverAction'>()(
  (theme, _params, classes) => ({
    actions: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      gap: theme.spacing(0.5),
      marginLeft: 'auto',
    },
    content: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: 8,
      minWidth: 0,
    },
    excludedContent: {
      opacity: 0.45,
    },
    excludedText: {
      textDecoration: 'line-through',
    },
    hoverAction: {
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    row: {
      [`&:focus-within .${classes.hoverAction}, &:hover .${classes.hoverAction}`]: { opacity: 1 },
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
      transition: 'opacity 0.2s',
    },
    /** One slot size for every row, so the icons line up down the list. */
    typeSlot: {
      '& svg': { height: 18, width: 18 },
      alignItems: 'center',
      color: theme.appColors.iconsType,
      display: 'flex',
      flexShrink: 0,
      height: 32,
      justifyContent: 'center',
      width: 32,
    },
  })
);

/**
 * Takes a column off the report about to be created. It excludes rather than
 * deletes: the row stays on the card struck through, nothing else in the org
 * changes, and the user can put the column back.
 */
export function ReportPlanExcludeButton({
  excluded,
  onToggle,
}: {
  excluded: boolean;
  onToggle: () => void;
}) {
  const { classes } = useReportPlanRowStyles();
  const title = excluded ? 'Undo exclude column' : 'Exclude column';

  if (excluded)
    return (
      <AlineTooltip arrow placement="top" title={title}>
        <IconButton size="small" onClick={onToggle}>
          <UndoIcon style={{ height: 18, width: 18 }} />
        </IconButton>
      </AlineTooltip>
    );

  return (
    <AlineTooltip arrow placement="top" title={title}>
      <IconButton className={classes.hoverAction} size="small" onClick={onToggle}>
        <CloseIcon style={{ height: 12, width: 12 }} />
      </IconButton>
    </AlineTooltip>
  );
}
